import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../constants';

const HELP_QS = [
  {
    q: 'Har du redan en HTML-fil eller vet du hur du vill bygga det tekniskt?',
    yes: 'verktyg',
    no:  null,
  },
  {
    q: 'Syftet är att visa en vision eller ett koncept för en kund?',
    yes: 'mockup',
    no:  null,
  },
  {
    q: 'Behöver slutresultatet faktiskt fungera — skicka data, räkna, spara?',
    yes: 'verktyg',
    no:  null,
  },
];

function HelpModal({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState([]);

  function answer(val) {
    const next = [...answers, val];
    setAnswers(next);

    const verdict = HELP_QS[step][val];
    if (verdict) {
      if (verdict === 'verktyg') { onClose(); navigate('/applab/upload'); }
      else { onClose(); navigate('/applab/mockup'); }
      return;
    }
    if (step < HELP_QS.length - 1) setStep(step + 1);
    else { onClose(); navigate('/applab/mockup'); }
  }

  function reset() { setStep(0); setAnswers([]); }

  const q = HELP_QS[step];
  return (
    <Dialog open={open} onClose={() => { onClose(); reset(); }}
      PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', minWidth: 380, borderRadius: 2 } }}>
      <DialogTitle sx={{ color: 'white', pb: 1 }}>Hjälp mig välja</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#ccc', fontSize: '0.95rem', mb: 2 }}>{q.q}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {['yes', 'no'].map(k => (
            <Box key={k} onClick={() => answer(k)} sx={{
              flex: 1, p: 2, borderRadius: 1.5, cursor: 'pointer', textAlign: 'center',
              border: `1px solid rgba(255,255,255,0.12)`,
              '&:hover': { borderColor: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '11' },
            }}>
              <Typography fontWeight={700} sx={{ color: 'white' }}>
                {k === 'yes' ? 'Ja' : 'Nej'}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 2, justifyContent: 'center' }}>
          {HELP_QS.map((_, i) => (
            <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: i === step ? APPLAB_ACCENT : 'rgba(255,255,255,0.2)' }} />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => { onClose(); reset(); }} sx={{ color: '#666', textTransform: 'none' }}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function NewAppChoice() {
  const navigate   = useNavigate();
  const [help, setHelp] = useState(false);

  const CARD_SX = {
    flex: 1, minWidth: 0, p: { xs: 3, md: 4.5 }, borderRadius: 3, cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.08)',
    bgcolor: 'rgba(255,255,255,0.03)',
    transition: 'all 0.18s',
    display: 'flex', flexDirection: 'column', gap: 1.5,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2 }}>
        <Box onClick={() => navigate('/applab')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#888', '&:hover': { color: 'white' }, fontSize: '0.85rem', width: 'fit-content' }}>
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, py: 6 }}>
        <Typography variant="h5" fontWeight={900} sx={{ mb: 1, textAlign: 'center' }}>
          Vad vill du skapa?
        </Typography>
        <Typography sx={{ color: '#666', fontSize: '0.9rem', mb: 5, textAlign: 'center' }}>
          Välj rätt verktyg för din uppgift.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, width: '100%', maxWidth: 1100, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Verktyg */}
          <Box onClick={() => navigate('/applab/upload')} sx={{
            ...CARD_SX,
            '&:hover': { borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)' },
          }}>
            <Typography fontSize="2.5rem" lineHeight={1}>🔧</Typography>
            <Box>
              <Typography fontWeight={800} fontSize="1.25rem" sx={{ mb: 0.5 }}>Verktyg</Typography>
              <Typography sx={{ color: APPLAB_ACCENT, fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
                Bygg en liten app som gör en uppgift
              </Typography>
              <Typography sx={{ color: '#777', fontSize: '0.82rem', lineHeight: 1.7 }}>
                Perfekt för egna interna verktyg, formulär, beräkningar och AI-hjälpmedel
                för vardagen. Du laddar upp en HTML-fil eller använder en mall.
              </Typography>
            </Box>
            <Box sx={{ mt: 'auto', pt: 1.5 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
                Kräver HTML-kunskaper
              </Typography>
            </Box>
          </Box>

          {/* MockupN */}
          <Box onClick={() => navigate('/applab/mockup')} sx={{
            ...CARD_SX,
            borderColor: APPLAB_ACCENT + '44',
            bgcolor: APPLAB_ACCENT + '08',
            '&:hover': { borderColor: APPLAB_ACCENT + '99', bgcolor: APPLAB_ACCENT + '14' },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography fontSize="2.5rem" lineHeight={1}>✨</Typography>
              <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '22', px: 1, py: 0.3, borderRadius: 1, border: `1px solid ${APPLAB_ACCENT}44` }}>NY</Box>
            </Box>
            <Box>
              <Typography fontWeight={800} fontSize="1.25rem" sx={{ mb: 0.5 }}>MockupN</Typography>
              <Typography sx={{ color: APPLAB_ACCENT, fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
                Skapa en visuell presentation för en kund
              </Typography>
              <Typography sx={{ color: '#777', fontSize: '0.82rem', lineHeight: 1.7 }}>
                AI bygger en övertygande dashboard eller projektportal utifrån din vision.
                Data är fejkad men ser äkta ut. Perfekt för offertmöten och kunddialoger.
              </Typography>
            </Box>
            <Box sx={{ mt: 'auto', pt: 1.5 }}>
              <Typography sx={{ color: APPLAB_ACCENT, fontSize: '0.78rem', fontWeight: 600 }}>
                Ingen kodkunskap behövs ✓
              </Typography>
            </Box>
          </Box>

          {/* MappN */}
          <Box onClick={() => navigate('/applab/mappn')} sx={{
            ...CARD_SX,
            borderColor: '#1565C044',
            bgcolor: '#1565C008',
            '&:hover': { borderColor: '#1565C099', bgcolor: '#1565C014' },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography fontSize="2.5rem" lineHeight={1}>🗺️</Typography>
              <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1565C0', bgcolor: '#1565C022', px: 1, py: 0.3, borderRadius: 1, border: '1px solid #1565C044' }}>NY</Box>
            </Box>
            <Box>
              <Typography fontWeight={800} fontSize="1.25rem" sx={{ mb: 0.5 }}>MappN</Typography>
              <Typography sx={{ color: '#42A5F5', fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
                Kartlägg systemarkitektur & plattformar
              </Typography>
              <Typography sx={{ color: '#777', fontSize: '0.82rem', lineHeight: 1.7 }}>
                Strukturera komponenter, perspektiv och funktioner i ett system. Estimera, länka till mockups och exportera till PDF.
              </Typography>
            </Box>
            <Box sx={{ mt: 'auto', pt: 1.5 }}>
              <Typography sx={{ color: '#42A5F5', fontSize: '0.78rem', fontWeight: 600 }}>
                Ingen kodkunskap behövs ✓
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          onClick={() => setHelp(true)}
          sx={{ mt: 3, color: '#555', fontSize: '0.82rem', cursor: 'pointer', '&:hover': { color: '#888' } }}
        >
          Osäker? Hjälp mig välja →
        </Box>
      </Box>

      <HelpModal open={help} onClose={() => setHelp(false)} />
    </Box>
  );
}
